import { FieldValues, UseFormReturn } from 'react-hook-form';
import { Form } from '../ui/form.js';
import { ZodSchema } from 'zod';
import { Temporal } from 'temporal-polyfill';
import { useDebouncedFormSubmit, UseDebouncedFormSubmitOptions } from '@/hooks/use-debounce-form.js';
interface AirFormProps<T extends FieldValues> {
  children: React.JSX.Element;
  form: UseFormReturn<T>;
  autosaveOptions?: AutosaveOptions<T>;
}

export interface AutosaveOptions<T> extends AutosaveOptionsBase {
  shortTermPersistent?: ShortTermPersistence<T>;
  longTermPersistent?: LongTermPersistence<T>;
}

interface AutosaveOptionsBase {
  delay: Temporal.Duration;
  isSubmitting?: boolean;
}

interface LongTermPersistence<T> {
  validator?: ZodSchema<T>;
  onSubmit: (props: T) => unknown | undefined;
}

interface ShortTermPersistence<T> {
  formKey: string;
  validator?: ZodSchema<T>;
  onSubmit?: (props: T) => unknown | undefined;
}

function AutoSave<T extends FieldValues>(props: AirFormProps<T>): React.JSX.Element {
  const { form, autosaveOptions, children } = props;

  const { delay, shortTermPersistent, longTermPersistent, isSubmitting } = autosaveOptions!;

  const debouncedProps: UseDebouncedFormSubmitOptions<T> = {
    form,
    delay,
    shortTermPersistent,
    longTermPersistent,
    isSubmitting,
  };

  useDebouncedFormSubmit(debouncedProps);

  return children;
}

export function AirForm<T extends FieldValues>(props: AirFormProps<T>): React.JSX.Element {
  const { children, form, autosaveOptions } = props;

  const isAutosave = autosaveOptions !== undefined;

  return (
    <Form {...form}>
      {isAutosave ? (
        <>
          <AutoSave form={form} autosaveOptions={autosaveOptions!}>
            {children}
          </AutoSave>
        </>
      ) : (
        <>{children}</>
      )}
    </Form>
  );
}
